import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "buttonText", "searchContainer", "searchButtonText", "searchInput", "results", "entityContainer"
  ]

  connect() {
    this.currentEntityType = null
  }

  selectEntity(event) {
    event.preventDefault()

    const entityType = event.currentTarget.dataset.entityType
    const searchPlaceholder = event.currentTarget.dataset.searchPlaceholder
    const entityName = event.currentTarget.textContent.trim()

    // Update the entity button text
    this.buttonTextTarget.textContent = entityName

    // Store current entity type
    this.currentEntityType = entityType

    // Close the entity dropdown menu
    const entityDropdownController = this.application.getControllerForElementAndIdentifier(
      this.entityContainerTarget,
      "dropdown"
    )
    if (entityDropdownController) {
      entityDropdownController.close()
    }

    // Show the search container
    this.searchContainerTarget.classList.remove("hidden")

    // Update search button text and input placeholder
    this.searchButtonTextTarget.textContent = searchPlaceholder
    this.searchInputTarget.placeholder = searchPlaceholder

    // Clear previous search
    this.searchInputTarget.value = ""
    this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Start typing to search...</div>'

    // Automatically expand the search dropdown - use a small delay to ensure the dropdown controller is initialized
    setTimeout(() => {
      const searchDropdownController = this.application.getControllerForElementAndIdentifier(
        this.searchContainerTarget,
        "dropdown"
      )
      if (searchDropdownController && searchDropdownController.menuTarget) {
        // Open the search dropdown menu by removing hidden class and setting up the click listener
        searchDropdownController.menuTarget.classList.remove("hidden")
        // Add the click listener so clicking outside will close it
        document.addEventListener("click", searchDropdownController.boundClose)

        // Focus on the search input
        this.searchInputTarget.focus()
      }
    }, 50)
  }

  search(event) {
    const query = event.target.value.trim()

    if (query.length < 2) {
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Start typing to search...</div>'
      return
    }

    // Show loading state
    this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Searching...</div>'

    // Perform search based on entity type
    this.performSearch(query, this.currentEntityType)
  }

  performSearch(query, entityType) {
    // Build the search URL based on entity type
    let searchUrl
    switch(entityType) {
      case 'album':
        searchUrl = `/albums/autocomplete?query=${encodeURIComponent(query)}`
        break
      case 'artist':
        searchUrl = `/artists/search?query=${encodeURIComponent(query)}`
        break
      case 'song':
        searchUrl = `/tracks/search?query=${encodeURIComponent(query)}`
        break
      case 'label':
        searchUrl = `/albums/label_search?query=${encodeURIComponent(query)}`
        break
      case 'organization':
        searchUrl = `/organizations/search?query=${encodeURIComponent(query)}`
        break
      default:
        return
    }

    // Fetch search results
    fetch(searchUrl, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displayResults(data, entityType)
    })
    .catch(error => {
      console.error('Search error:', error)
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-red-500">Error loading results</div>'
    })
  }

  displayResults(data, entityType) {
    if (!data || data.length === 0) {
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No results found</div>'
      return
    }

    let resultsHtml = ''
    data.forEach(item => {
      let displayText = ''
      let subtitle = ''
      let resultHtml = ''

      switch(entityType) {
        case 'album':
          displayText = item.title
          subtitle = item.upc ? `(${item.upc})` : '(No UPC)'

          // Album result with thumbnail
          const thumbnailUrl = item.cover_image_thumbnail || '/assets/album-placeholder.svg'
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->entity-search#selectResult" data-result-id="${item.id}" data-result-name="${displayText}">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <img src="${thumbnailUrl}"
                       alt="${displayText}"
                       class="w-12 h-12 rounded object-cover"
                       onerror="this.onerror=null; this.src='/assets/album-placeholder.svg';">
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">${displayText}</div>
                  <div class="text-xs text-gray-500">${subtitle}</div>
                </div>
              </div>
            </a>
          `
          break

        case 'artist':
          displayText = item.name

          // Artist result with Spotify thumbnail or placeholder
          if (item.spotify_thumbnail_uri) {
            resultHtml = `
              <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->entity-search#selectResult" data-result-id="${item.id}" data-result-name="${displayText}">
                <div class="flex items-center gap-3">
                  <div class="flex-shrink-0">
                    <img src="${item.spotify_thumbnail_uri}"
                         alt="${displayText}"
                         class="w-12 h-12 rounded object-cover">
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 truncate">${displayText}</div>
                  </div>
                </div>
              </a>
            `
          } else {
            resultHtml = `
              <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->entity-search#selectResult" data-result-id="${item.id}" data-result-name="${displayText}">
                <div class="flex items-center gap-3">
                  <div class="flex-shrink-0 w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                    <svg class="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 truncate">${displayText}</div>
                  </div>
                </div>
              </a>
            `
          }
          break

        case 'song':
          displayText = item.title
          subtitle = item.isrc ? `${item.isrc}` : 'No ISRC'
          const songThumbnailUrl = item.album_thumbnail || '/assets/album-placeholder.svg'

          // Song result with album thumbnail
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->entity-search#selectResult" data-result-id="${item.id}" data-result-name="${displayText}">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <img src="${songThumbnailUrl}"
                       alt="${item.album_title || 'Album'}"
                       class="w-12 h-12 rounded object-cover"
                       onerror="this.onerror=null; this.src='/assets/album-placeholder.svg';">
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">${displayText}</div>
                  <div class="text-xs text-gray-500">${subtitle}</div>
                </div>
              </div>
            </a>
          `
          break

        case 'label':
          displayText = item.label_name
          subtitle = item.album_count ? `${item.album_count} albums` : ''
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->entity-search#selectResult" data-result-name="${displayText}">
              <div class="text-sm font-medium text-gray-900">${displayText}</div>
              ${subtitle ? `<div class="text-xs text-gray-500">${subtitle}</div>` : ''}
            </a>
          `
          break

        case 'organization':
          displayText = item.title
          subtitle = 'Organization'
          resultHtml = `
            <a href="#" class="block px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-action="click->entity-search#selectResult" data-result-id="${item.id}" data-result-name="${displayText}">
              <div class="text-sm font-medium text-gray-900">${displayText}</div>
              ${subtitle ? `<div class="text-xs text-gray-500">${subtitle}</div>` : ''}
            </a>
          `
          break
      }

      resultsHtml += resultHtml
    })

    this.resultsTarget.innerHTML = resultsHtml
  }

  selectResult(event) {
    event.preventDefault()

    const resultId = event.currentTarget.dataset.resultId
    const resultName = event.currentTarget.dataset.resultName

    // Navigate to trends page with entity parameter using Turbo
    let url
    if (this.currentEntityType === 'album') {
      url = `/trends?album_id=${resultId}`
    } else if (this.currentEntityType === 'artist') {
      url = `/trends?artist_id=${resultId}`
    } else if (this.currentEntityType === 'song') {
      url = `/trends?song_id=${resultId}`
    } else if (this.currentEntityType === 'label') {
      url = `/trends?label_name=${encodeURIComponent(resultName)}`
    } else if (this.currentEntityType === 'organization') {
      url = `/trends?organization_id=${resultId}`
    }

    if (url) {
      // Use Turbo to navigate - this will update the URL and re-render the page
      Turbo.visit(url)
    }
  }

}
